/**
 * LensContext.jsx - Platform Lens State Management
 * 
 * GRANULLO_MODE_LENS_UNIFICATION_v1.0 - PAUSE_GATE_1
 * 
 * Manages the platform-wide "Lens" that determines whether the user
 * is viewing Granullo through a Systems lens, Money lens, or Both.
 * 
 * CANONICAL STATEMENT:
 * "Granullo operates in two modes:
 *  - When intent is executed through systems
 *  - When intent is executed through money"
 * 
 * LENS VALUES:
 * - SYSTEMS: Focus on system governance, evidence preservation, execution flows
 * - MONEY: Focus on economic exposure, preparedness value, financial accountability
 * - BOTH: Unified view showing both perspectives (default after user has seen both)
 * 
 * PERSISTENCE:
 * - URL query param: ?lens=systems|money|both (shareable)
 * - localStorage: granullo_lens (sticky across sessions)
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// =============================================================================
// CONSTANTS
// =============================================================================

export const LENS_VALUES = {
  SYSTEMS: 'systems',
  MONEY: 'money',
  BOTH: 'both'
};

export const LENS_OPTIONS = [
  {
    id: LENS_VALUES.SYSTEMS,
    label: 'Systems',
    shortLabel: 'Systems',
    description: 'How intent flows through execution',
    icon: 'Workflow',
    color: 'blue'
  },
  {
    id: LENS_VALUES.MONEY,
    label: 'Money',
    shortLabel: 'Money',
    description: 'How intent flows through capital',
    icon: 'DollarSign',
    color: 'emerald'
  },
  {
    id: LENS_VALUES.BOTH,
    label: 'Both',
    shortLabel: 'Both',
    description: 'Unified infrastructure view',
    icon: 'Layers',
    color: 'violet'
  }
];

// Storage keys
export const LENS_STORAGE_KEY = 'granullo_lens';
export const LENS_SEEN_SYSTEMS_KEY = 'granullo_lens_seen_systems';
export const LENS_SEEN_MONEY_KEY = 'granullo_lens_seen_money';

// Default lens
export const DEFAULT_LENS = LENS_VALUES.BOTH;

// =============================================================================
// CONTEXT
// =============================================================================

const LensContext = createContext({
  lens: DEFAULT_LENS,
  setLens: () => {},
  hasSeenSystems: false,
  hasSeenMoney: false,
  markLensSeen: () => {},
  canUseBoth: false,
  lensOption: null,
  isSystemsLens: false,
  isMoneyLens: false,
  isBothLens: false
});

// =============================================================================
// PROVIDER
// =============================================================================

export const LensProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Track which lenses have been viewed
  const [hasSeenSystems, setHasSeenSystems] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(LENS_SEEN_SYSTEMS_KEY) === 'true';
  });
  
  const [hasSeenMoney, setHasSeenMoney] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(LENS_SEEN_MONEY_KEY) === 'true';
  });
  
  // Initialize lens from URL param, localStorage, or default
  const [lens, setLensState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LENS;
    
    // Priority: URL param > localStorage > default
    const urlLens = searchParams.get('lens');
    if (urlLens && Object.values(LENS_VALUES).includes(urlLens)) {
      return urlLens;
    }
    
    const storedLens = localStorage.getItem(LENS_STORAGE_KEY);
    if (storedLens && Object.values(LENS_VALUES).includes(storedLens)) {
      return storedLens;
    }
    
    return DEFAULT_LENS;
  });
  
  // User can use "BOTH" only after seeing both individual lenses
  const canUseBoth = hasSeenSystems && hasSeenMoney;
  
  // Set lens with persistence and URL sync
  const setLens = useCallback((newLens) => {
    if (!Object.values(LENS_VALUES).includes(newLens)) {
      console.warn(`Invalid lens value: ${newLens}`);
      return;
    }
    
    // If trying to set BOTH but hasn't seen both lenses, default to SYSTEMS
    if (newLens === LENS_VALUES.BOTH && !canUseBoth) {
      newLens = LENS_VALUES.SYSTEMS;
    }
    
    setLensState(newLens);
    
    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(LENS_STORAGE_KEY, newLens);
    }
    
    // Update URL param (preserve other params)
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lens', newLens);
    setSearchParams(newParams, { replace: true });
    
    // Dispatch event for non-React listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('lensChanged', { detail: { lens: newLens } }));
    }
  }, [canUseBoth, searchParams, setSearchParams]);
  
  // Mark a lens as seen
  const markLensSeen = useCallback((lensValue) => {
    if (typeof window === 'undefined') return;
    
    if (lensValue === LENS_VALUES.SYSTEMS) {
      localStorage.setItem(LENS_SEEN_SYSTEMS_KEY, 'true');
      setHasSeenSystems(true);
    } else if (lensValue === LENS_VALUES.MONEY) {
      localStorage.setItem(LENS_SEEN_MONEY_KEY, 'true');
      setHasSeenMoney(true);
    }
  }, []);
  
  // Sync lens from URL on navigation
  useEffect(() => {
    const urlLens = searchParams.get('lens');
    if (urlLens && Object.values(LENS_VALUES).includes(urlLens)) {
      // Don't allow BOTH if user hasn't earned it
      if (urlLens === LENS_VALUES.BOTH && !canUseBoth) {
        setLensState(LENS_VALUES.SYSTEMS);
      } else if (urlLens !== lens) {
        setLensState(urlLens);
        localStorage.setItem(LENS_STORAGE_KEY, urlLens);
      }
    }
  }, [searchParams, canUseBoth, lens]);
  
  // Auto-mark current lens as seen
  useEffect(() => {
    if (lens === LENS_VALUES.SYSTEMS) {
      markLensSeen(LENS_VALUES.SYSTEMS);
    } else if (lens === LENS_VALUES.MONEY) {
      markLensSeen(LENS_VALUES.MONEY);
    }
  }, [lens, markLensSeen]);
  
  // Get current lens option metadata
  const lensOption = LENS_OPTIONS.find(opt => opt.id === lens) || LENS_OPTIONS[0];
  
  // Convenience booleans
  const isSystemsLens = lens === LENS_VALUES.SYSTEMS;
  const isMoneyLens = lens === LENS_VALUES.MONEY;
  const isBothLens = lens === LENS_VALUES.BOTH;
  
  const value = {
    lens,
    setLens,
    hasSeenSystems,
    hasSeenMoney,
    markLensSeen,
    canUseBoth,
    lensOption,
    isSystemsLens,
    isMoneyLens,
    isBothLens,
    LENS_VALUES,
    LENS_OPTIONS
  };
  
  return (
    <LensContext.Provider value={value}>
      {children}
    </LensContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================

export const useLens = () => {
  const context = useContext(LensContext);
  if (!context) {
    throw new Error('useLens must be used within LensProvider');
  }
  return context;
};

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Get lens from localStorage (for non-React usage)
 */
export const getLensFromStorage = () => {
  if (typeof window === 'undefined') return DEFAULT_LENS;
  return localStorage.getItem(LENS_STORAGE_KEY) || DEFAULT_LENS;
};

/**
 * Build URL with lens param
 */
export const buildLensUrl = (path, lens) => {
  const url = new URL(path, window.location.origin);
  url.searchParams.set('lens', lens);
  return url.pathname + url.search;
};

/**
 * Reset all lens state (for testing)
 */
export const resetLensState = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(LENS_STORAGE_KEY);
  localStorage.removeItem(LENS_SEEN_SYSTEMS_KEY);
  localStorage.removeItem(LENS_SEEN_MONEY_KEY);
};

export default LensContext;
